import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { ProjectElement } from '../../../../resources/element/project-element.interface';
import { ModalComponent } from '../../modal.component';
import { TranslateService } from '../../../../utilities/translate/translate.service';
import * as Diff from 'text-diff';
import { StripHtmlPipe } from '../../../../utilities/strip-html/strip-html.pipe';
import { UnescapePipe } from '../../../../utilities/unescape/unescape.pipe';
import { FormControl } from '@angular/forms';
import { ThrowStmt } from '@angular/compiler';
import { ConstantsService } from '../../../../utilities/constants/constants.service';
import { ConstantsQualityLevel } from '../../../../utilities/constants/constants.interface';
import { AuthService } from '../../../../auth/auth.service';

/**
 * Modal for restoring the paragraph's text to the template value.
 */
@Component({
  selector: 'sg2-restore-paragraph',
  templateUrl: './restore-paragraph.component.html',
  styleUrls: ['./restore-paragraph.component.less']
})
export class ModalRestoreParagraphComponent implements OnInit {

  /** Reference to template value element in HTML. */
  @ViewChild('templateValueSelected') templateValueRef: ElementRef;

  /** Current active element. */
  public element: ProjectElement;

  /** Element value with marked differences, in HTML format. */
  public elementValueDiffs: string;

  /** Quality level dropdown. */
  public qualityLevelForm = new FormControl();

  /** Element quality levels. */
  public qualityLevels: ConstantsQualityLevel[] = [];

  /** The element's template value. */
  public templateValue: ProjectElement['value'];

  constructor(
    @Inject(MAT_DIALOG_DATA) public config: { element: ProjectElement },
    public authService: AuthService,
    private constantsService: ConstantsService,
    public dialogRef: MatDialogRef<ModalRestoreParagraphComponent>,
    private matDialog: MatDialog,
    private stripHtmlPipe: StripHtmlPipe,
    private translateService: TranslateService,
    private unescapePipe: UnescapePipe
  ) {
    this.element = config.element;
  }

  ngOnInit() {
    this.qualityLevels = this.constantsService.getQualityLevels();
    this._setTemplateValue();
    this._getDifference();
    this._setQualityLevelForm();
    this._onQualityLevelFormChange();
  }

  /**
   * Submit and restore.
   *
   * Opens a second confirm modal.
   *
   * @note The confirm modal would ideally open through the modalService, but that
   *  triggers a circular dependency.
   */
  public submit(): void {
    this.matDialog.open(ModalComponent, { data: {
      type: 'primary',
      title: this.translateService.get('restoreTemplateValue'),
      content: this.translateService.get('restoreTemplateValueContent').replace('? ', '?<br>'),
      actions: [{
        action: 'restore',
        label: this.translateService.getPlural('restore')
      }] }
    }).afterClosed().subscribe(result => {
      if (result === 'restore') {
        this.dialogRef.close(this.qualityLevelForm.value);
      }
    });
  }

  /**
   * Calculate difference between two values.
   *
   * Create a diff object by comparing two element values. After comparing,
   * the diff object is 'cleaned up', for improved human readable results.
   *
   * To extract two seperate texts from the results, the template value only
   * keeps the `<del>`s and the current value keeps the `<ins>`s.
   */
  private _getDifference(): void {
    const diff = new Diff();
    const textDiff = diff.main(
      this.stripHtmlPipe.transform(this.unescapePipe.transform(this.templateValue), true),
      this.stripHtmlPipe.transform(this.unescapePipe.transform(this.element.value), true),
    );
    diff.cleanupSemantic(textDiff);
    diff.cleanupEfficiency(textDiff);
    const diffs = this.unescapePipe.transform((diff.prettyHtml(textDiff)));

    this.templateValue = diffs.replace(/<ins>.*?<\/ins>/g, '');
    this.elementValueDiffs = diffs.replace(/<del>.*?<\/del>/g, '');
  }

  /**
   * On quality level change.
   */
  private _onQualityLevelFormChange(): void {
    this.qualityLevelForm.valueChanges.subscribe(quality => {
      this._setTemplateValue(quality);
      this._getDifference();
    });
  }

  /**
   * Set current active template value.
   *
   * Adds a flash animation when changing, so it's clear that the template value has changed, even
   * when the value is the same. Otherwise it might look like the dropdown doesn't work.
   *
   * @param [qualityLevel=this.element.quality] The quality level of the template. Defaults to
   *  the active element's quality level.
   */
  private _setTemplateValue(qualityLevel: ProjectElement['quality'] = this.element.quality): void {
    const template = this.element.templateValues.find(tv => tv.quality === qualityLevel);
    this.templateValue = template.value;
    if (this.templateValueRef) {
      this.templateValueRef.nativeElement.classList.add('animated', 'flash');
      setTimeout(() => {
        this.templateValueRef.nativeElement.classList.remove('animated', 'flash');
      }, 200);
    }
  }

  /**
   * Initialize quality level form.
   */
  private _setQualityLevelForm(): void {
    this.qualityLevelForm.patchValue(this.element.quality);
  }
}
